import React, { useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Loader2, Inbox, MailOpen, AlertTriangle,
  ArrowDownLeft, ArrowUpRight, Clock, RefreshCw
} from 'lucide-react';
import { formatDate } from '../../../lib/utils';

const STATUS_BADGE = {
  sent: { label: 'Envoyé', cls: 'bg-green-50 text-green-700 border-green-200' },
  pending: { label: 'En cours', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  queued: { label: 'En file', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  failed: { label: 'Échec', cls: 'bg-red-50 text-red-700 border-red-200' },
  received: { label: 'Reçu', cls: 'bg-tp-accent/10 text-tp-accent border-tp-accent/20' },
  draft: { label: 'Brouillon', cls: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const CandidateMessagesPanel = ({ candidateId, candidateEmail, candidateName }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const loadMessages = async (silent = false) => {
    if (!candidateId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api.get(`/admin/messages/candidate/${candidateId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) {
      console.error('Failed to load candidate messages:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [candidateId]);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Sujet et message requis');
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/admin/messages/candidate/${candidateId}/send`, {
        subject: subject.trim(),
        body: body.trim(),
      });
      if (res.ok) {
        toast.success('Message envoyé');
        setSubject('');
        setBody('');
        setComposeOpen(false);
        setTimeout(() => loadMessages(true), 700);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Impossible d'envoyer le message");
      }
    } catch (e) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const sentCount = messages.filter(m => m.direction === 'sent').length;
  const receivedCount = messages.filter(m => m.direction === 'received').length;

  return (
    <Card className="border-tp-border" data-testid="candidate-messages-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-tp-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-tp-primary/10 flex items-center justify-center">
            <MessageSquare className="h-5 w-5 text-tp-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-tp-text">Messagerie</h3>
            <p className="text-xs text-tp-text-light">
              {messages.length} message{messages.length > 1 ? 's' : ''}
              {sentCount > 0 && ` · ${sentCount} envoyé${sentCount > 1 ? 's' : ''}`}
              {receivedCount > 0 && ` · ${receivedCount} reçu${receivedCount > 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => loadMessages(true)}
            disabled={refreshing}
            data-testid="candidate-msg-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            size="sm"
            onClick={() => setComposeOpen(o => !o)}
            className="bg-tp-accent hover:bg-tp-accent-light text-white"
            data-testid="candidate-msg-compose-toggle"
          >
            <Send className="h-4 w-4 mr-1.5" /> {composeOpen ? 'Annuler' : 'Nouveau'}
          </Button>
        </div>
      </div>

      {/* Compose panel */}
      {composeOpen && (
        <div className="p-4 md:p-5 border-b border-tp-border bg-tp-bg/30 space-y-3" data-testid="candidate-msg-compose">
          <div className="text-xs text-tp-text-light">
            À : <span className="font-medium text-tp-text">{candidateName || candidateEmail}</span>
            {candidateEmail && <span className="text-gray-500"> &lt;{candidateEmail}&gt;</span>}
          </div>
          <Input
            placeholder="Objet du message"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={sending}
            data-testid="candidate-msg-subject"
          />
          <Textarea
            placeholder="Votre message..."
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={sending}
            data-testid="candidate-msg-body"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setComposeOpen(false); setSubject(''); setBody(''); }} disabled={sending}>
              Annuler
            </Button>
            <Button
              size="sm"
              onClick={handleSend}
              disabled={sending || !subject.trim() || !body.trim()}
              className="bg-tp-accent hover:bg-tp-accent-light text-white"
              data-testid="candidate-msg-send"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              Envoyer
            </Button>
          </div>
        </div>
      )}

      {/* Messages list */}
      <div className="p-4 md:p-5">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-tp-accent" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10" data-testid="candidate-msg-empty">
            <Inbox className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-tp-text-light">Aucun message pour ce candidat pour l'instant.</p>
            <p className="text-xs text-gray-400 mt-1">Envoyez-en un depuis le bouton "Nouveau" ci-dessus.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="candidate-msg-list">
            {messages.map((m) => {
              const isReceived = m.direction === 'received';
              const badge = STATUS_BADGE[m.status] || STATUS_BADGE.sent;
              const isExpanded = expandedId === m.id;
              return (
                <div
                  key={m.id}
                  className={`rounded-lg border p-3 ${isReceived ? 'bg-tp-accent/5 border-tp-accent/20' : 'bg-white border-tp-border'}`}
                  data-testid={`candidate-msg-item-${m.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isReceived ? 'bg-tp-accent/20 text-tp-accent' : 'bg-tp-primary/10 text-tp-primary'}`}>
                      {isReceived ? <ArrowDownLeft className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-medium text-tp-text truncate">{m.subject || '(Sans objet)'}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${badge.cls}`}>
                          {badge.label}
                        </span>
                        {m.has_error && (
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500" title={m.error_message} />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-tp-text-light mb-1.5">
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(m.created_at)}</span>
                        <span>·</span>
                        <span className="truncate">
                          {isReceived ? `De : ${m.from_email}` : `À : ${m.to_email}`}
                        </span>
                      </div>
                      <p
                        className={`text-xs text-tp-text-light ${isExpanded ? 'whitespace-pre-wrap' : 'line-clamp-2'} cursor-pointer`}
                        onClick={() => setExpandedId(isExpanded ? null : m.id)}
                      >
                        {m.body || '(Pas de contenu)'}
                      </p>
                      {m.body && m.body.length > 120 && (
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : m.id)}
                          className="text-[11px] text-tp-accent hover:underline mt-1"
                        >
                          {isExpanded ? 'Réduire' : 'Lire plus'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CandidateMessagesPanel;
